// Tabela de clientes — desktop ≥ 768px.
// Em < 768px vira lista de cards verticais (DESIGN_SYSTEM §responsive).
//
// Cada linha:
//  - avatar (iniciais) + nome + email
//  - documento formatado (CPF/CNPJ) — tnum
//  - status badge (dot + texto)
//  - origens badges (multi)
//  - data criada — tnum
//  - menu (...)

import { MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatBR } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { formatDocument } from '@control-center/shared';

import type { ClientEntity, OriginBadge } from '../../lib/clients-api';

import { ClientStatusBadge } from './ClientStatusBadge';

const ORIGIN_LABEL: Record<OriginBadge, string> = {
  owl_managed: 'Owl',
  saas_direct: 'Direct',
  manual: 'Manual',
  bootstrap: 'Import',
};

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '·'
  );
}

function OriginBadges({ items }: { items: OriginBadge[] }): JSX.Element {
  if (!items || items.length === 0) {
    return <span className="text-[11px] text-muted-3">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-[6px]">
      {items.map((origin) => (
        <span
          key={origin}
          className="inline-flex items-center rounded-[4px] border border-line bg-panel-2 px-[6px] py-[2px] text-[10px] font-medium text-muted"
        >
          {ORIGIN_LABEL[origin] ?? origin}
        </span>
      ))}
    </div>
  );
}

function formatDate(iso: string): string {
  return formatBR(iso, { day: '2-digit', month: 'short', year: 'numeric' });
}

interface ClientTableProps {
  clients: ClientEntity[];
  onSelect?: (client: ClientEntity) => void;
}

export function ClientTable({ clients, onSelect }: ClientTableProps): JSX.Element {
  const navigate = useNavigate();

  function handleOpen(client: ClientEntity): void {
    if (onSelect) onSelect(client);
    else navigate(`/identities/${client.id}`);
  }

  return (
    <div className="rounded-[14px] border border-line bg-panel">
      {/* Desktop ≥ 768 */}
      <div className="hidden md:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-line bg-panel-2 text-[12px] font-medium text-muted-2">
              <th className="px-4 py-[10px]">Nome</th>
              <th className="px-4 py-[10px]">Documento</th>
              <th className="px-4 py-[10px]">Status</th>
              <th className="px-4 py-[10px]">Origem</th>
              <th className="px-4 py-[10px]">Criado em</th>
              <th className="w-10 px-4 py-[10px]" aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr
                key={client.id}
                onClick={() => handleOpen(client)}
                tabIndex={0}
                role="button"
                aria-label={`Abrir cliente ${client.name}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen(client);
                  }
                }}
                className={cn(
                  'cursor-pointer border-b border-line text-[13px] text-ink-2 transition-colors',
                  'hover:bg-panel-2 focus:bg-panel-2 focus:outline-none',
                )}
              >
                <td className="px-4 py-[12px]">
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden
                      className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-muted to-muted-3 text-[10px] font-semibold text-white"
                    >
                      {initials(client.name)}
                    </span>
                    <div className="flex min-w-0 flex-col leading-[1.25]">
                      <span className="truncate font-medium text-ink">{client.name}</span>
                      <span className="truncate text-[11px] text-muted-2">
                        {client.email ?? '—'}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="tnum px-4 py-[12px] text-[12px] text-ink-2">
                  {client.document ? (
                    formatDocument(client.document)
                  ) : (
                    <span className="text-muted-3">—</span>
                  )}
                </td>
                <td className="px-4 py-[12px]">
                  <ClientStatusBadge status={client.status} />
                </td>
                <td className="px-4 py-[12px]">
                  <OriginBadges items={client.origins_badges ?? []} />
                </td>
                <td className="tnum px-4 py-[12px] text-[12px] text-muted">
                  {formatDate(client.created_at)}
                </td>
                <td className="px-4 py-[12px]">
                  <button
                    type="button"
                    aria-label={`Ações de ${client.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen(client);
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
                  >
                    <MoreHorizontal size={15} strokeWidth={2} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile < 768 — lista de cards */}
      <ul className="flex flex-col md:hidden">
        {clients.map((client) => (
          <li key={client.id}>
            <button
              type="button"
              onClick={() => handleOpen(client)}
              className="flex w-full flex-col gap-3 border-b border-line px-4 py-[14px] text-left transition-colors hover:bg-panel-2"
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-muted to-muted-3 text-[12px] font-semibold text-white"
                >
                  {initials(client.name)}
                </span>
                <div className="flex min-w-0 flex-col leading-[1.25]">
                  <span className="truncate text-[14px] font-medium text-ink">{client.name}</span>
                  <span className="truncate text-[12px] text-muted-2">{client.email ?? '—'}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 text-[12px]">
                <ClientStatusBadge status={client.status} />
                <span className="tnum text-muted">
                  {client.document ? formatDocument(client.document) : '—'}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <OriginBadges items={client.origins_badges ?? []} />
                <span className="tnum text-[11px] text-muted-2">
                  {formatDate(client.created_at)}
                </span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ClientTableSkeleton({ rows = 6 }: { rows?: number }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-line bg-panel" aria-hidden>
      <div className="hidden md:block">
        <div className="grid grid-cols-[1.4fr_1fr_0.8fr_1fr_0.8fr_40px] gap-4 border-b border-line bg-panel-2 px-4 py-[10px] text-[12px] text-muted-2">
          <span>Nome</span>
          <span>Documento</span>
          <span>Status</span>
          <span>Origem</span>
          <span>Criado em</span>
          <span />
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="grid grid-cols-[1.4fr_1fr_0.8fr_1fr_0.8fr_40px] items-center gap-4 border-b border-line px-4 py-[14px]"
          >
            <div className="flex items-center gap-3">
              <span className="h-[28px] w-[28px] shrink-0 animate-pulse rounded-full bg-panel-3" />
              <div className="flex flex-col gap-[6px]">
                <span className="h-[10px] w-32 animate-pulse rounded bg-panel-3" />
                <span className="h-[8px] w-20 animate-pulse rounded bg-panel-3" />
              </div>
            </div>
            <span className="h-[10px] w-28 animate-pulse rounded bg-panel-3" />
            <span className="h-[10px] w-16 animate-pulse rounded bg-panel-3" />
            <span className="h-[10px] w-20 animate-pulse rounded bg-panel-3" />
            <span className="h-[10px] w-20 animate-pulse rounded bg-panel-3" />
            <span className="h-[10px] w-8 animate-pulse rounded bg-panel-3" />
          </div>
        ))}
      </div>
      <div className="flex flex-col md:hidden">
        {Array.from({ length: Math.min(rows, 4) }).map((_, i) => (
          <div key={i} className="flex flex-col gap-3 border-b border-line px-4 py-[14px]">
            <div className="flex items-center gap-3">
              <span className="h-[34px] w-[34px] shrink-0 animate-pulse rounded-full bg-panel-3" />
              <div className="flex w-full flex-col gap-[6px]">
                <span className="h-[10px] w-40 animate-pulse rounded bg-panel-3" />
                <span className="h-[8px] w-32 animate-pulse rounded bg-panel-3" />
              </div>
            </div>
            <span className="h-[10px] w-24 animate-pulse rounded bg-panel-3" />
          </div>
        ))}
      </div>
    </div>
  );
}
